import styled from 'styled-components';
import { ReactComponent as EmptyStateIcon } from '@elementary/common-ui/assets/24x24/empty-state.svg';
import {
  Button,
  EmptyStateGraphic,
  FlexColumn,
} from '@elementary/common-ui/components';
import { Button as MuiButton, Typography, useTheme } from '@mui/material';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import {
  componentToken,
  radius,
  space,
} from '@elementary/common-ui/style/utils';

type Variant = 'regular' | 'big' | 'wide';

const Wrapper = styled(FlexColumn)`
  justify-content: center;
  align-items: center;
`;

const Message = styled(Typography)`
  color: ${componentToken('emptyState.message.color')};
  font-size: ${componentToken('emptyState.compactMessage.fontSize')};
`;

const Graphic = () => (
  <EmptyStateGraphic kind="data" fallback={<EmptyStateIcon />} />
);

type Props = {
  variant?: Variant;
};

const emptyStateMessage = 'There is no data to display';

type ButtonProps = {
  height?: number;
  fontSize?: number;
};

// Looks that draw the empty-state icon pair it with a secondary action.
const AddTestButton = ({ height = 24, fontSize = 13 }: ButtonProps) => {
  const navigate = useNavigateWithEnv();
  const { icon } = useTheme().tokens.components.emptyState;
  const onClick = () => navigate('/test-management');

  if (icon) {
    return (
      <Button variant="outlined" size="extraSmall" onClick={onClick}>
        Add tests
      </Button>
    );
  }

  return (
    <MuiButton
      onClick={onClick}
      sx={{
        height,
        fontSize,
        background: 'surface.highlight',
        color: 'accent.secondary',
        borderRadius: radius('xxs'),
      }}
    >
      Add tests
    </MuiButton>
  );
};

const EmptyState = ({ variant = 'regular' }: Props) => {
  if (variant === 'big') {
    return (
      <Wrapper gap={space('xl')}>
        <Graphic />
        <Typography>Coming soon</Typography>
      </Wrapper>
    );
  }

  if (variant === 'wide') {
    return (
      <Wrapper sx={{ height: '115px' }}>
        <Graphic />
        <Message
          sx={{
            marginTop: space('sm'),
            marginBottom: space('sm'),
          }}
        >
          {emptyStateMessage}
        </Message>
        <AddTestButton height={17} fontSize={10} />
      </Wrapper>
    );
  }

  return (
    <Wrapper sx={{ height: '156px' }}>
      <Graphic />
      <Message
        sx={{
          marginTop: space('sm'),
          marginBottom: space('lg'),
        }}
      >
        {emptyStateMessage}
      </Message>
      <AddTestButton />
    </Wrapper>
  );
};

export default EmptyState;
